import React, { useState } from 'react';
import { Box, Typography, Stack } from '@mui/material';
import {
  PyrenzBlueButton,
  PyrenzMessageBox,
  PyrenzColorPicker,
  PyrenzModal,
  PyrenzModalContent,
  PyrenzNumberInput,
  PyrenzTextInput,
} from '@client/theme';
import { useUserStore } from '@client/store';
import {
  messageCustomizationConfig,
  SectionConfig,
  FieldConfig,
} from '@client/components/config';

interface MessageCustomizationModalProps {
  open: boolean;
  onClose: () => void;
}

export function MessageCustomizationModal({
  open,
  onClose,
}: MessageCustomizationModalProps) {
  const { customization, setCustomization } = useUserStore();
  const [localCustomization, setLocalCustomization] = useState({
    ...customization,
  });

  const handleFieldChange = (key: string, value: any) => {
    const updated = { ...localCustomization, [key]: value };
    setLocalCustomization(updated);
    setCustomization(updated);
  };

  const handleReset = () => {
    const defaults: Record<string, any> = {};
    messageCustomizationConfig.forEach((section: SectionConfig) => {
      Object.assign(defaults, section.defaultValues);
    });
    setLocalCustomization(defaults as typeof customization);
    setCustomization(defaults as typeof customization);
  };

  return (
    <PyrenzModal open={open} onClose={onClose}>
      <PyrenzModalContent>
        <Typography variant="h6" gutterBottom>
          Chat Appearance
        </Typography>
        <Stack spacing={2}>
          {messageCustomizationConfig.map((section: SectionConfig) => (
            <Box key={section.id}>
              <Typography variant="subtitle1" gutterBottom>
                {section.title}
              </Typography>
              <Stack spacing={1}>
                {section.fields.map((field: FieldConfig) => {
                  const value = localCustomization[field.key];
                  switch (field.type) {
                    case 'color':
                      return (
                        <Stack
                          key={field.key}
                          direction="row"
                          alignItems="center"
                          justifyContent="space-between"
                        >
                          <Typography variant="body2">{field.label}</Typography>
                          <PyrenzColorPicker
                            color={value || '#ffffff'}
                            onChange={(v) => handleFieldChange(field.key, v)}
                          />
                        </Stack>
                      );
                    case 'number':
                      return (
                        <Stack
                          key={field.key}
                          direction="row"
                          alignItems="center"
                          justifyContent="space-between"
                        >
                          <Typography variant="body2">{field.label}</Typography>
                          <PyrenzNumberInput
                            value={value || 0}
                            min={field.min}
                            max={field.max}
                            step={field.step}
                            onChange={(v) => handleFieldChange(field.key, v)}
                          />
                        </Stack>
                      );
                    case 'text':
                      return (
                        <Stack
                          key={field.key}
                          direction="row"
                          alignItems="center"
                          justifyContent="space-between"
                        >
                          <Typography variant="body2">{field.label}</Typography>
                          <PyrenzTextInput
                            value={value || ''}
                            onChange={(v) => handleFieldChange(field.key, v)}
                          />
                        </Stack>
                      );
                    case 'json':
                      return null;
                    case 'textarea':
                      return null;
                    case 'slider':
                      return null;
                  }
                  return null;
                })}
              </Stack>
            </Box>
          ))}
        </Stack>
        <PyrenzBlueButton onClick={handleReset} fullWidth sx={{ mt: 2 }}>
          Reset to Default
        </PyrenzBlueButton>
        <PyrenzBlueButton onClick={onClose} fullWidth sx={{ mt: 2 }}>
          Save Changes
        </PyrenzBlueButton>
      </PyrenzModalContent>
    </PyrenzModal>
  );
}
